import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '../components';
import { custom } from '../custom';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { getCurrentLocation } from '../utils/locationUtils';
import { compressImage } from '../utils/imageUtils';
import {
  MdCalendarMonth, MdLocationOn, MdAddPhotoAlternate, MdDelete,
  MdCheckCircle, MdError, MdWarning, MdClose
} from 'react-icons/md';

const CalendarIcon = MdCalendarMonth as any;
const LocationIcon = MdLocationOn as any;
const AddPhotoIcon = MdAddPhotoAlternate as any;
const DeleteIcon = MdDelete as any;
const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;
const CloseIcon = MdClose as any;

export const CreateEventActivity: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t } = useLanguage();
  const te = (t as any).eventActivity || {};

  const getDefaultDatetime = () => {
    const now = new Date();
    // Gunakan waktu lokal device (bukan UTC) agar sesuai timezone user
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  const [date, setDate] = useState(getDefaultDatetime());
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [address, setAddress] = useState('');
  const [locationError, setLocationError] = useState('');
  const [isFetchingLocation, setIsFetchingLocation] = useState(true);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandPreview, setExpandPreview] = useState<string | null>(null);

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean; title: string; message: string;
    type: 'success' | 'error' | 'warning'; onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  useEffect(() => {
    setTimeout(() => { window.scroll({ top: -1, left: 0, behavior: 'smooth' }); }, 10);
  }, [pathname]);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const loc = await getCurrentLocation({ addressFormat: 'full' });
        setLocation(`${loc.lat},${loc.lng}`);
        setAddress(loc.address || (t.attendance?.addressNotFound || 'Address not found'));
      } catch (err: any) {
        setLocationError(err.message || 'Failed to get location. Please enable GPS.');
      } finally {
        setIsFetchingLocation(false);
      }
    };
    fetchLocation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({ isOpen: true, title, message, type, onClose });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig(prev => ({ ...prev, isOpen: false }));
    if (onClose) onClose();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    const processed = await Promise.all(files.map(async f => {
      if (f.type.startsWith('image/')) {
        try { return await compressImage(f); } catch { return f; }
      }
      return f;
    }));
    setImageFiles(prev => [...prev, ...processed]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    if (!date) {
      showAlert(t.common?.warning || 'Warning', te.validation?.fillRequired || 'Date is required.', 'warning');
      return;
    }
    if (isFetchingLocation) {
      showAlert(t.common?.warning || 'Warning', te.waitingLocation || 'Please wait, getting location...', 'warning');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('date', date);
      if (description) formData.append('description', description);
      if (location) formData.append('location', location);
      if (address) formData.append('address', address);
      imageFiles.forEach(f => formData.append('images', f));

      await axiosInstance.post('/event-activities', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      showAlert(
        t.common?.success || 'Success',
        te.alerts?.success || 'Event activity created successfully!',
        'success',
        () => navigate('/EventActivity', { replace: true })
      );
    } catch (err) {
      console.error('Failed to create event activity:', err);
      showAlert(t.common?.error || 'Error', te.alerts?.failed || 'Failed to create event.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderAlert = () => {
    if (!alertConfig.isOpen) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <div style={{ backgroundColor: 'white', borderRadius: 20, padding: 28, width: '100%', maxWidth: 320, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ marginBottom: 16 }}>
            {alertConfig.type === 'success' && <CheckCircleIcon size={56} color="#10B981" />}
            {alertConfig.type === 'error' && <ErrorIcon size={56} color="#EF4444" />}
            {alertConfig.type === 'warning' && <WarningIcon size={56} color="#F59E0B" />}
          </div>
          <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
          <text.T16 style={{ marginBottom: 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>{alertConfig.message}</text.T16>
          <components.Button title={t.common?.ok || 'OK'} onClick={hideAlert} containerStyle={{ width: '100%' }} />
        </div>
      </div>
    );
  };

  const renderExpandModal = () => {
    if (!expandPreview) return null;
    return (
      <div onClick={() => setExpandPreview(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '100%' }}>
          <div onClick={() => setExpandPreview(null)} style={{ position: 'absolute', top: -44, right: 0, cursor: 'pointer', padding: 8 }}>
            <CloseIcon size={24} color="white" />
          </div>
          <img src={expandPreview} alt="preview" style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }} />
        </div>
      </div>
    );
  };

  return (
    <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <components.Header title={te.createTitle || 'Create Event Activity'} goBack={true} />

      <main style={{ marginTop: 62, height: '100vh', overflowY: 'auto', paddingBottom: 85 }} className="container">

        {/* Card: Date & Description */}
        <div style={{ backgroundColor: 'white', padding: 20, borderRadius: 16, border: '1px solid #F3F4F6', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <div style={{ width: 4, height: 16, backgroundColor: '#6366F1', borderRadius: 4 }} />
            <text.H5 style={{ color: theme.colors.mainDark }}>{te.dateTime || 'Date & Time'}</text.H5>
          </div>

          <div style={{ marginBottom: 0 }}>
            <text.T14 style={{ color: '#6B7280', marginBottom: 6, display: 'block' }}>
              {te.dateTime || 'Date & Time'} <span style={{ color: '#EF4444' }}>*</span>
            </text.T14>
            <custom.InputField
              placeholder="YYYY-MM-DDTHH:MM"
              value={date}
              onChange={(e: any) => setDate(e.target.value)}
              leftIcon={<CalendarIcon size={20} color="#9CA3AF" />}
              type={"datetime-local" as any}
              containerStyle={{ marginBottom: 0 }}
            />
          </div>
        </div>

        {/* Card: Description */}
        <div style={{ backgroundColor: 'white', padding: 20, borderRadius: 16, border: '1px solid #F3F4F6', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 4, height: 16, backgroundColor: '#F59E0B', borderRadius: 4 }} />
            <text.H5 style={{ color: theme.colors.mainDark }}>{te.description || 'Description'}</text.H5>
          </div>
          <div style={{ width: '100%', padding: '14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12 }}>
            <textarea
              placeholder={te.descriptionPlaceholder || 'Describe the event...'}
              value={description}
              onChange={e => setDescription(e.target.value)}
              style={{
                width: '100%', minHeight: 100,
                ...theme.fonts.SourceSansPro_400Regular,
                outline: 'none', border: 'none', resize: 'none',
                backgroundColor: 'transparent', fontSize: 14, lineHeight: 1.6
              }}
            />
          </div>
        </div>

        {/* Card: Location */}
        <div style={{ backgroundColor: 'white', padding: 20, borderRadius: 16, border: '1px solid #F3F4F6', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 4, height: 16, backgroundColor: '#10B981', borderRadius: 4 }} />
            <text.H5 style={{ color: theme.colors.mainDark }}>{te.location || 'Location'}</text.H5>
          </div>
          <div style={{ padding: 14, backgroundColor: '#F0FDF4', borderRadius: 12, border: '1px solid #A7F3D0', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ marginTop: 2 }}><LocationIcon size={22} color="#10B981" /></div>
            <div style={{ flex: 1 }}>
              {isFetchingLocation ? (
                <text.T14 style={{ color: '#9CA3AF', fontStyle: 'italic' }}>{t.common?.loading || 'Fetching location...'}</text.T14>
              ) : locationError ? (
                <text.T14 style={{ color: '#EF4444' }}>{locationError}</text.T14>
              ) : (
                <>
                  <text.T14 style={{ color: theme.colors.mainDark, fontWeight: '500', display: 'block', lineHeight: 1.5 }}>{address}</text.T14>
                  <text.T14 style={{ color: '#6B7280', fontSize: 11, marginTop: 4, display: 'block' }}>{location}</text.T14>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Card: Photos */}
        <div style={{ backgroundColor: 'white', padding: 20, borderRadius: 16, border: '1px solid #F3F4F6', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 4, height: 16, backgroundColor: '#EC4899', borderRadius: 4 }} />
              <text.H5 style={{ color: theme.colors.mainDark }}>{te.photos || 'Photos'}</text.H5>
            </div>
            <text.T14 style={{ color: '#9CA3AF', fontSize: 12 }}>{imageFiles.length} / 10</text.T14>
          </div>

          {/* Image previews — tombol add selalu di kiri */}
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
            {imageFiles.length < 10 && (
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: 90, height: 90, flexShrink: 0, borderRadius: 10,
                  border: '2px dashed #C7D2FE', backgroundColor: '#EEF2FF',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', cursor: 'pointer', gap: 4
                }}
              >
                <AddPhotoIcon size={26} color="#6366F1" />
                <text.T14 style={{ fontSize: 10, color: '#6366F1', fontWeight: '600', textAlign: 'center' }}>
                  {te.addPhotos || 'Add Photos'}
                </text.T14>
              </div>
            )}
            {imageFiles.map((file, idx) => (
              <div key={idx} style={{ position: 'relative', width: 90, height: 90, flexShrink: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                <img
                  src={URL.createObjectURL(file)}
                  alt=""
                  onClick={() => setExpandPreview(URL.createObjectURL(file))}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                />
                <div
                  onClick={() => handleRemoveImage(idx)}
                  style={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: '50%', padding: 3, cursor: 'pointer', display: 'flex' }}
                >
                  <DeleteIcon size={14} color="white" />
                </div>
              </div>
            ))}
          </div>
          <input type="file" multiple accept="image/*" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
        </div>

      </main>

      {/* Bottom button */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: 'white', padding: '16px 24px 32px', boxShadow: '0 -4px 20px rgba(0,0,0,0.06)', zIndex: 10 }}>
        <div className="container" style={{ padding: 0, maxWidth: 650, margin: '0 auto' }}>
          <components.Button
            title={loading ? (te.saving || 'Saving...') : (te.submit || 'Create Event')}
            onClick={handleCreate}
          />
        </div>
      </div>

      {renderAlert()}
      {renderExpandModal()}
    </div>
  );
};
